<script lang="ts">
	import defaultOgImage from "@assets/images/og-image.png?webp&h=630&imagetools";
	import AuthenticationModal from "@components/AuthenticationModal.svelte";
	import CartOverviewModal from "@components/cart/CartOverviewModal.svelte";
	import BreadcrumbListStructuredData from "@components/common/BreadcrumbListStructuredData.svelte";
	import { Auth } from "@utils/Auth.svelte.js";
	import { createTitle } from "@utils/createTitle.js";
	import { getCookie } from "@utils/getCookie.js";
	import { type Snippet, onMount, tick } from "svelte";
	import InterVariableFont from "@core/assets/fonts/Inter/InterVariable.woff2";
	import { tenants } from "@core/config/tenants.js";
	import { Country } from "@core/schema/Country.js";
	import { Currency } from "@core/schema/Currency.js";
	import { EconomicSubjectVatType } from "@core/schema/EconomicSubjectVatType.js";
	import type { Media } from "@core/schema/Media.js";
	import { appName } from "@core/schema/names/appName.js";
	import { isEnum } from "@core/utils/enum/isEnum.js";
	import { thumbUploadedFile } from "@core/utils/thumbUploadedFile.js";
	import "../../assets/css/main.css";
	import { Cart } from "../../cart/Cart.svelte.js";
	import { getGlobalSettings } from "../../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../../contexts/getGlobalTemplateProps.js";
	import { currencyCookieName } from "../../currencyCookieName.js";
	import { defaultDestinationCountry } from "../../defaultDestinationCountry.js";
	import { destinationCountryCookieName } from "../../destinationCountryCookieName.js";
	import { lockScroll } from "../../state/lockScroll.svelte.js";
	import { selectedCurrency } from "../../state/selectedCurrency.svelte.js";
	import { selectedDestinationCountry } from "../../state/selectedDestinationCountry.svelte.js";
	import Breadcrumbs from "./Breadcrumbs.svelte";
	import Cookies from "./Cookies.svelte";
	import Footer from "./Footer.svelte";
	import GoogleAnalytics from "./GoogleAnalytics.svelte";
	import HeadFavicons from "./HeadFavicons.svelte";
	import Header from "./Header.svelte";

	interface PreloadFont {
		type: string;
		href: string;
	}

	const {
		ogImage = defaultOgImage,
		children,
		transparentHeader = false,
		pageHasMobileBottomBar = false,
	}: {
		ogImage?: string | Media;
		children: Snippet;
		transparentHeader?: boolean;
		pageHasMobileBottomBar?: boolean;
	} = $props();

	const settings = getGlobalSettings();
	const { WEB_URL, FILE_BUCKET_URL_MAP, ROBOTS } = settings;
	const {
		breadcrumbs,
		countrySettings,
		currency,
		description,
		destinationCountry,
		index,
		keywords,
		pagePath,
		pricingLevel,
		subcategories,
		tenant,
		title,
		exchangeRatesSetting,
	} = getGlobalTemplateProps();

	const pageTitle = createTitle(title);

	const fontsToPreload: PreloadFont[] = [{ type: "font/woff2", href: InterVariableFont }];
	const canonical = tenant.locale in pagePath ? `${WEB_URL}${pagePath[tenant.locale]}` : undefined;
	const themeColor = "#5E2828";
	const ogImagePath = $derived(
		typeof ogImage === "object" ? thumbUploadedFile(1200, 630, ogImage, FILE_BUCKET_URL_MAP, null) : ogImage,
	);

	$effect(() => {
		document.documentElement.style.overflow = lockScroll.locked ? "hidden" : "auto";
	});

	selectedCurrency.current = currency !== "fromCookies" ? currency : null;
	selectedDestinationCountry.current = destinationCountry !== "fromCookies" ? destinationCountry : null;

	onMount(async () => {
		void tick().then(() => {
			if (window.location.hash) {
				const element = document.querySelector(window.location.hash);
				element?.scrollIntoView();
			}
		});

		await Auth.init(settings);

		let finalDestinationCountry = destinationCountry;
		const destinationFromCookie = await getCookie(destinationCountryCookieName);
		if (isEnum(Country)(destinationFromCookie)) {
			finalDestinationCountry = destinationFromCookie;
		}
		if (finalDestinationCountry === "fromCookies") {
			finalDestinationCountry = defaultDestinationCountry;
		}
		selectedDestinationCountry.current = finalDestinationCountry;
		let finalCurrency = currency;
		const currencyfromCookie = await getCookie(currencyCookieName);
		if (isEnum(Currency)(currencyfromCookie)) {
			finalCurrency = currencyfromCookie;
		}
		if (finalCurrency === "fromCookies") {
			finalCurrency = tenant.defaultCurrency;
		}
		selectedCurrency.current = finalCurrency;

		await Cart.init(settings, {
			destinationCountry: finalDestinationCountry,
			currency: finalCurrency,
			countrySettings,
			pricingLevel,
			tenant,
			subcategories,
			exchangeRates: exchangeRatesSetting?.exchangeRates ?? [],
			vatType: EconomicSubjectVatType.NotVatSubject,
		});
	});
</script>

<svelte:head>
	<!-- Preloads of fonts used at the top of pages -->
	{#each fontsToPreload as { href, type } (href)}
		<link rel="preload" as="font" {href} {type} crossorigin="anonymous" />
	{/each}

	<!-- Theme color -->
	<meta name="msapplication-TileColor" content={themeColor} />
	<meta name="theme-color" content={themeColor} />

	<!-- Site name -->
	<meta property="og:site_name" content={appName} />
	<meta name="twitter:site" content={appName} />
	<meta name="application-name" content={appName} />
	<meta name="apple-mobile-web-app-title" content={appName} />

	<!-- Dynamic Open graph tags (Meta) -->
	{#if canonical}
		<meta property="og:url" content={canonical} />
	{/if}
	<meta property="og:image" content={ogImagePath} />

	<!-- Dynamic Twitter Meta tags -->
	<meta name="twitter:card" content="summary_large_image" />
	<meta property="twitter:domain" content={WEB_URL} />
	{#if canonical}
		<meta property="twitter:url" content={canonical} />
	{/if}
	<meta name="twitter:image" content={ogImagePath} />

	<!-- Title, description and keywords -->
	<title>{pageTitle}</title>
	<meta property="title" content={pageTitle} />
	<meta property="og:title" content={pageTitle} />
	<meta name="twitter:title" content={pageTitle} />
	<meta name="description" content={description} />
	<meta name="og:description" content={description} />
	<meta name="twitter:description" content={description} />
	{#if keywords}
		<meta name="keywords" content={keywords} />
	{/if}

	<!-- Robots -->
	<meta name="robots" content={ROBOTS === "index" && index ? "index, follow" : "noindex, nofollow"} />

	<!-- Canonical and hreflang -->
	{#if canonical}
		<link rel="canonical" href={canonical} />
	{/if}
	{#each Object.values(tenants) as tenant (tenant.locale)}
		{#if tenant.locale in pagePath}
			<link rel="alternate" hreflang={tenant.locale} href="{WEB_URL}{pagePath[tenant.locale]}" />
		{/if}
	{/each}
</svelte:head>

<HeadFavicons />

<BreadcrumbListStructuredData {breadcrumbs} canonicalUrl={canonical} />

<GoogleAnalytics />

<div class="flex flex-col min-h-full">
	<Header transparent={transparentHeader} />

	<main class="grid-container grow relative">
		<Breadcrumbs {breadcrumbs} currentPath={pagePath[tenant.locale]} />

		{@render children()}
	</main>

	<Footer {pageHasMobileBottomBar} />
</div>

<AuthenticationModal />
<CartOverviewModal />
<Cookies />
